"use client";

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@repo/ui/components/ui/select";
import { Field, FieldError, FieldLabel } from "@repo/ui/form-fields";
import { Tags } from "lucide-react";
import { useFieldContext } from "@/shared/forms/form-context";

/** Service type option */
export interface ServiceTypeOption {
  code: string;
  display: string;
}

export interface ServiceTypeFieldProps {
  label?: string;
  placeholder?: string;
  /** Service types from useServiceTypes() hook */
  serviceTypes: ServiceTypeOption[];
  disabled?: boolean;
  isLoading?: boolean;
}

export function ServiceTypeField({
  label,
  placeholder = "Select service type...",
  serviceTypes,
  disabled,
  isLoading,
}: ServiceTypeFieldProps) {
  const field = useFieldContext<string | undefined>();

  return (
    <Field errors={field.state.meta.errors} isTouched={field.state.meta.isTouched}>
      {label && <FieldLabel>{label}</FieldLabel>}
      <Select
        value={field.state.value ?? ""}
        onValueChange={(value) => {
          field.handleChange(value || undefined);
          field.handleBlur();
        }}
        disabled={disabled || isLoading}
        items={Object.fromEntries(serviceTypes.map((st) => [st.code, st.display]))}
      >
        <SelectTrigger className={`w-full ${label ? "mt-1" : ""}`} disabled={disabled || isLoading}>
          <SelectValue placeholder={isLoading ? "Loading..." : placeholder} />
        </SelectTrigger>
        <SelectContent>
          {serviceTypes.length === 0 ? (
            <div className="flex items-center gap-2 px-2 py-4 text-sm text-muted-foreground justify-center">
              <Tags className="h-4 w-4" />
              No service types configured
            </div>
          ) : (
            serviceTypes.map((st) => (
              <SelectItem key={st.code} value={st.code}>
                {st.display}
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>
      <FieldError />
    </Field>
  );
}
